<template>
  <div class="bg-white rounded">
    <ul class="nav nav-tabs nav-tabs-bottom nav-justified flex-wrap mb-3">
      <li class="nav-item" v-for="tab in tabs" :key="tab.key">
        <button type="button" :class="tabClass(tab.key)" @click="switchTab(tab.key)">
          <i :class="`${tab.icon} me-1`"></i>{{ tab.label }}
        </button>
      </li>
    </ul>
  </div>

  <div v-if="activeTab === 'activities'" class="card border-0">
    <div class="card-header d-flex align-items-center justify-content-between">
      <h5 class="mb-0">Activities</h5>
      <button type="button" class="btn btn-sm btn-white" :disabled="loading.activities" @click="loadActivities">Refresh</button>
    </div>
    <div class="card-body">
      <div v-if="loading.activities" class="border rounded p-3 text-muted">Loading activities...</div>
      <div v-else-if="!activities.length" class="border rounded p-3 text-muted">No activity has been recorded for this company yet.</div>
      <div v-else class="d-flex flex-column gap-3">
        <div v-for="item in activities" :key="item.id" class="border rounded p-3">
          <div class="d-flex align-items-start">
            <span class="avatar avatar-md avatar-rounded flex-shrink-0 bg-skyblue me-2">
              <i :class="`${activityIcon(item.type)} fs-20`"></i>
            </span>
            <div class="flex-fill">
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <h6 class="fw-medium mb-1">{{ item.title }}</h6>
                <span class="text-muted small">{{ formatDate(item.occurred_at) }}</span>
              </div>
              <p class="mb-1 text-dark">{{ safeText(item.description, "—") }}</p>
              <div class="small text-muted">
                <span>{{ userDisplayName(item.user) }}</span>
                <span v-if="item.meta?.status"> • {{ item.meta.status }}</span>
                <span v-if="item.meta?.outcome"> • Outcome: {{ item.meta.outcome }}</span>
                <span v-if="item.meta?.duration_minutes"> • {{ item.meta.duration_minutes }} min</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="activeTab === 'notes'" class="card border-0">
    <div class="card-header d-flex align-items-center justify-content-between">
      <h5 class="mb-0">Notes</h5>
      <button type="button" class="btn btn-sm btn-white" :disabled="loading.notes" @click="loadNotes">Refresh</button>
    </div>
    <div class="card-body">
      <div class="border rounded p-3">
        <h6 class="fw-medium mb-2">{{ editingNoteId ? "Edit note" : "Add note" }}</h6>
        <div class="mb-3">
          <textarea v-model="noteForm.body" class="form-control" rows="4" placeholder="Add an internal note about this company"></textarea>
        </div>
        <div class="d-flex align-items-center justify-content-end gap-2">
          <button v-if="editingNoteId" type="button" class="btn btn-outline-light border" @click="resetNoteForm">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="submitting.note" @click="submitNote">
            <span v-if="submitting.note" class="spinner-border spinner-border-sm me-2"></span>
            {{ editingNoteId ? "Update Note" : "Save Note" }}
          </button>
        </div>
      </div>
      <div class="mt-3">
        <div v-if="loading.notes" class="border rounded p-3 text-muted">Loading notes...</div>
        <div v-else-if="!notes.length" class="border rounded p-3 text-muted">No notes have been added yet.</div>
        <div v-else class="d-flex flex-column gap-3">
          <div v-for="note in notes" :key="note.id" class="border rounded p-3">
            <div class="d-flex align-items-start justify-content-between gap-3">
              <div class="flex-fill">
                <p class="mb-2 text-dark">{{ note.body }}</p>
                <div class="small text-muted">{{ userDisplayName(note.user) }} • {{ formatDate(note.created_at) }}</div>
              </div>
              <div class="d-flex align-items-center gap-2">
                <button type="button" class="btn btn-sm btn-outline-primary" @click="startEditNote(note)">Edit</button>
                <button type="button" class="btn btn-sm btn-outline-danger" :disabled="submitting.noteDeleteId === note.id" @click="deleteNote(note)">Delete</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="activeTab === 'calls'" class="card border-0">
    <div class="card-header d-flex align-items-center justify-content-between">
      <h5 class="mb-0">Calls</h5>
      <button type="button" class="btn btn-sm btn-white" :disabled="loading.calls" @click="loadCalls">Refresh</button>
    </div>
    <div class="card-body">
      <div class="border rounded p-3">
        <h6 class="fw-medium mb-2">{{ editingCallId ? "Edit call log" : "Log a call" }}</h6>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">Subject</label>
            <input v-model="callForm.subject" type="text" class="form-control" />
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Call Date & Time</label>
            <input v-model="callForm.call_datetime" type="datetime-local" class="form-control" />
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Duration (minutes)</label>
            <input v-model="callForm.duration_minutes" type="number" min="0" class="form-control" />
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Outcome</label>
            <input v-model="callForm.outcome" type="text" class="form-control" />
          </div>
          <div class="col-12 mb-3">
            <label class="form-label">Notes</label>
            <textarea v-model="callForm.notes" class="form-control" rows="4"></textarea>
          </div>
        </div>
        <div class="d-flex align-items-center justify-content-end gap-2">
          <button v-if="editingCallId" type="button" class="btn btn-outline-light border" @click="resetCallForm">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="submitting.call" @click="submitCall">
            <span v-if="submitting.call" class="spinner-border spinner-border-sm me-2"></span>
            {{ editingCallId ? "Update Call" : "Save Call" }}
          </button>
        </div>
      </div>
      <div class="mt-3">
        <div v-if="loading.calls" class="border rounded p-3 text-muted">Loading calls...</div>
        <div v-else-if="!calls.length" class="border rounded p-3 text-muted">No call logs have been recorded yet.</div>
        <div v-else class="d-flex flex-column gap-3">
          <div v-for="call in calls" :key="call.id" class="border rounded p-3">
            <div class="d-flex align-items-start justify-content-between gap-3">
              <div class="flex-fill">
                <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <h6 class="fw-medium mb-1">{{ call.subject }}</h6>
                  <span class="text-muted small">{{ formatDate(call.call_datetime) }}</span>
                </div>
                <p class="mb-2 text-dark">{{ safeText(call.notes, "No notes added.") }}</p>
                <div class="small text-muted">
                  {{ userDisplayName(call.user) }}
                  <span v-if="call.duration_minutes"> • {{ call.duration_minutes }} min</span>
                  <span v-if="call.outcome"> • {{ call.outcome }}</span>
                </div>
              </div>
              <div class="d-flex align-items-center gap-2">
                <button type="button" class="btn btn-sm btn-outline-primary" @click="startEditCall(call)">Edit</button>
                <button type="button" class="btn btn-sm btn-outline-danger" :disabled="submitting.callDeleteId === call.id" @click="deleteCall(call)">Delete</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="activeTab === 'files'" class="card border-0">
    <div class="card-header d-flex align-items-center justify-content-between">
      <h5 class="mb-0">Files</h5>
      <button type="button" class="btn btn-sm btn-white" :disabled="loading.files" @click="loadFiles">Refresh</button>
    </div>
    <div class="card-body">
      <div class="border rounded p-3">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div>
            <h6 class="fw-medium mb-1">Upload a file</h6>
            <p class="mb-0 text-muted">Attach documents relevant to this company. Maximum file size is 10 MB.</p>
          </div>
          <div class="d-flex align-items-center gap-2">
            <input ref="fileInput" type="file" class="d-none" @change="handleFileSelection" />
            <button type="button" class="btn btn-outline-primary" @click="openFilePicker">Choose File</button>
            <button type="button" class="btn btn-primary" :disabled="!selectedFile || submitting.file" @click="uploadSelectedFile">
              <span v-if="submitting.file" class="spinner-border spinner-border-sm me-2"></span>
              Upload
            </button>
          </div>
        </div>
        <div v-if="selectedFile" class="small text-muted mt-2">Selected: {{ selectedFile.name }}</div>
      </div>
      <div class="mt-3">
        <div v-if="loading.files" class="border rounded p-3 text-muted">Loading files...</div>
        <div v-else-if="!files.length" class="border rounded p-3 text-muted">No files have been uploaded yet.</div>
        <div v-else class="d-flex flex-column gap-3">
          <div v-for="file in files" :key="file.id" class="border rounded p-3">
            <div class="d-flex align-items-start justify-content-between gap-3">
              <div class="flex-fill">
                <div class="fw-medium">{{ file.file_name }}</div>
                <div class="small text-muted">
                  {{ userDisplayName(file.user) }} • {{ formatDate(file.created_at) }}
                  <span v-if="file.size"> • {{ formatBytes(file.size) }}</span>
                </div>
                <div v-if="file.mime_type" class="small text-muted mt-1">{{ file.mime_type }}</div>
              </div>
              <div class="d-flex align-items-center gap-2">
                <button type="button" class="btn btn-sm btn-outline-primary" @click="downloadFile(file)">Download</button>
                <button type="button" class="btn btn-sm btn-outline-danger" :disabled="submitting.fileDeleteId === file.id" @click="deleteFile(file)">Delete</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="card border-0">
    <div class="card-header d-flex align-items-center justify-content-between">
      <h5 class="mb-0">Email</h5>
      <button type="button" class="btn btn-sm btn-white" :disabled="loading.emails" @click="loadEmails">Refresh</button>
    </div>
    <div class="card-body">
      <div class="border rounded p-3">
        <h6 class="fw-medium mb-2">Compose email</h6>
        <div class="row">
          <div class="col-12 mb-3">
            <label class="form-label">To</label>
            <input v-model="emailForm.to_email" type="email" class="form-control" :placeholder="defaultEmail || 'recipient@example.com'" />
          </div>
          <div class="col-12 mb-3">
            <label class="form-label">Subject</label>
            <input v-model="emailForm.subject" type="text" class="form-control" />
          </div>
          <div class="col-12 mb-3">
            <label class="form-label">Message</label>
            <textarea v-model="emailForm.body" class="form-control" rows="6"></textarea>
          </div>
        </div>
        <div class="d-flex align-items-center justify-content-end">
          <button type="button" class="btn btn-primary" :disabled="submitting.email" @click="submitEmail">
            <span v-if="submitting.email" class="spinner-border spinner-border-sm me-2"></span>
            Send Email
          </button>
        </div>
      </div>
      <div class="mt-3">
        <div v-if="loading.emails" class="border rounded p-3 text-muted">Loading emails...</div>
        <div v-else-if="!emails.length" class="border rounded p-3 text-muted">No emails have been logged yet.</div>
        <div v-else class="d-flex flex-column gap-3">
          <div v-for="email in emails" :key="email.id" class="border rounded p-3">
            <div class="d-flex align-items-start justify-content-between gap-3">
              <div class="flex-fill">
                <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <h6 class="fw-medium mb-1">{{ email.subject }}</h6>
                  <span class="badge" :class="email.status === 'sent' ? 'badge-soft-success' : 'badge-soft-danger'">{{ email.status }}</span>
                </div>
                <div class="small text-muted mb-2">To {{ email.to_email }} • {{ formatDate(email.sent_at || email.created_at) }}</div>
                <p class="mb-2 text-dark whitespace-pre-line">{{ safeText(email.body, "—") }}</p>
                <div class="small text-muted">{{ userDisplayName(email.user) }}</div>
                <div v-if="email.error_message" class="small text-danger mt-1">{{ email.error_message }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import companyEngagementService from "@/services/company-engagement.service";
import { toast } from "@/utils/toast";

const props = defineProps({
  companyId: { type: [Number, String], required: true },
  defaultEmail: { type: String, default: "" },
});

const tabs = [
  { key: "activities", label: "Activities", icon: "ti ti-activity" },
  { key: "notes", label: "Notes", icon: "ti ti-file-description" },
  { key: "calls", label: "Calls", icon: "ti ti-phone-call" },
  { key: "files", label: "Files", icon: "ti ti-files" },
  { key: "email", label: "Email", icon: "ti ti-mail-check" },
];

const activeTab = ref("activities");
const activities = ref([]);
const notes = ref([]);
const calls = ref([]);
const files = ref([]);
const emails = ref([]);
const selectedFile = ref(null);
const fileInput = ref(null);
const editingNoteId = ref(null);
const editingCallId = ref(null);

const loading = ref({ activities: false, notes: false, calls: false, files: false, emails: false });
const submitting = ref({ note: false, noteDeleteId: null, call: false, callDeleteId: null, file: false, fileDeleteId: null, email: false });
const noteForm = ref({ body: "" });
const callForm = ref({ subject: "", call_datetime: "", duration_minutes: "", outcome: "", notes: "" });
const emailForm = ref({ to_email: "", subject: "", body: "" });

const validCompanyId = computed(() => Number(props.companyId) || null);

function tabClass(tab) {
  return ["nav-link", "fw-medium", "d-flex", "align-items-center", "justify-content-center", activeTab.value === tab ? "active" : ""];
}

function switchTab(tab) {
  activeTab.value = tab;
  if (tab === "activities") loadActivities();
  if (tab === "notes") loadNotes();
  if (tab === "calls") loadCalls();
  if (tab === "files") loadFiles();
  if (tab === "email") loadEmails();
}

function userDisplayName(user) {
  return user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email || "System";
}

function safeText(value, fallback = "-") {
  return String(value || "").trim() || fallback;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let index = 0;
  let size = value;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  return `${size.toFixed(size >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
}

function activityIcon(type) {
  switch ((type || "").toLowerCase()) {
    case "note": return "ti ti-notes";
    case "call": return "ti ti-phone-call";
    case "file": return "ti ti-file-text";
    case "email": return "ti ti-mail";
    default: return "ti ti-activity";
  }
}

function resetNoteForm() {
  editingNoteId.value = null;
  noteForm.value = { body: "" };
}

function startEditNote(note) {
  editingNoteId.value = note.id;
  noteForm.value = { body: note.body || "" };
}

function resetCallForm() {
  editingCallId.value = null;
  callForm.value = { subject: "", call_datetime: "", duration_minutes: "", outcome: "", notes: "" };
}

function startEditCall(call) {
  editingCallId.value = call.id;
  callForm.value = {
    subject: call.subject || "",
    call_datetime: toDateTimeLocal(call.call_datetime),
    duration_minutes: call.duration_minutes ?? "",
    outcome: call.outcome || "",
    notes: call.notes || "",
  };
  switchTab("calls");
}

function toDateTimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function openFilePicker() { fileInput.value?.click(); }
function handleFileSelection(event) { selectedFile.value = event.target.files?.[0] || null; }

async function loadActivities() {
  if (!validCompanyId.value) return;
  loading.value.activities = true;
  try {
    const res = await companyEngagementService.getActivities(validCompanyId.value);
    activities.value = Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to load activities.", "error");
  } finally {
    loading.value.activities = false;
  }
}

async function loadNotes() {
  if (!validCompanyId.value) return;
  loading.value.notes = true;
  try {
    const res = await companyEngagementService.getNotes(validCompanyId.value);
    notes.value = Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to load notes.", "error");
  } finally {
    loading.value.notes = false;
  }
}

async function loadCalls() {
  if (!validCompanyId.value) return;
  loading.value.calls = true;
  try {
    const res = await companyEngagementService.getCalls(validCompanyId.value);
    calls.value = Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to load calls.", "error");
  } finally {
    loading.value.calls = false;
  }
}

async function loadFiles() {
  if (!validCompanyId.value) return;
  loading.value.files = true;
  try {
    const res = await companyEngagementService.getFiles(validCompanyId.value);
    files.value = Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to load files.", "error");
  } finally {
    loading.value.files = false;
  }
}

async function loadEmails() {
  if (!validCompanyId.value) return;
  loading.value.emails = true;
  try {
    const res = await companyEngagementService.getEmails(validCompanyId.value);
    emails.value = Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to load emails.", "error");
  } finally {
    loading.value.emails = false;
  }
}

async function submitNote() {
  if (!validCompanyId.value) return;
  const body = String(noteForm.value.body || "").trim();
  if (!body) return toast("Note body is required.", "error");
  submitting.value.note = true;
  try {
    if (editingNoteId.value) {
      await companyEngagementService.updateNote(validCompanyId.value, editingNoteId.value, { body });
      toast("Note updated.");
    } else {
      await companyEngagementService.createNote(validCompanyId.value, { body });
      toast("Note added.");
    }
    resetNoteForm();
    await Promise.all([loadNotes(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to save note.", "error");
  } finally {
    submitting.value.note = false;
  }
}

async function deleteNote(note) {
  if (!validCompanyId.value || !note?.id) return;
  if (!window.confirm("Delete this note?")) return;
  submitting.value.noteDeleteId = note.id;
  try {
    await companyEngagementService.deleteNote(validCompanyId.value, note.id);
    toast("Note deleted.");
    if (editingNoteId.value === note.id) resetNoteForm();
    await Promise.all([loadNotes(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to delete note.", "error");
  } finally {
    submitting.value.noteDeleteId = null;
  }
}

async function submitCall() {
  if (!validCompanyId.value) return;
  const payload = {
    subject: String(callForm.value.subject || "").trim(),
    call_datetime: callForm.value.call_datetime,
    duration_minutes: callForm.value.duration_minutes === "" ? null : Number(callForm.value.duration_minutes || 0),
    outcome: String(callForm.value.outcome || "").trim() || null,
    notes: String(callForm.value.notes || "").trim() || null,
  };
  if (!payload.subject) return toast("Call subject is required.", "error");
  if (!payload.call_datetime) return toast("Call date and time are required.", "error");
  submitting.value.call = true;
  try {
    if (editingCallId.value) {
      await companyEngagementService.updateCall(validCompanyId.value, editingCallId.value, payload);
      toast("Call updated.");
    } else {
      await companyEngagementService.createCall(validCompanyId.value, payload);
      toast("Call saved.");
    }
    resetCallForm();
    await Promise.all([loadCalls(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to save call.", "error");
  } finally {
    submitting.value.call = false;
  }
}

async function deleteCall(call) {
  if (!validCompanyId.value || !call?.id) return;
  if (!window.confirm("Delete this call log?")) return;
  submitting.value.callDeleteId = call.id;
  try {
    await companyEngagementService.deleteCall(validCompanyId.value, call.id);
    toast("Call deleted.");
    if (editingCallId.value === call.id) resetCallForm();
    await Promise.all([loadCalls(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to delete call.", "error");
  } finally {
    submitting.value.callDeleteId = null;
  }
}

async function uploadSelectedFile() {
  if (!validCompanyId.value || !selectedFile.value) return;
  submitting.value.file = true;
  try {
    await companyEngagementService.uploadFile(validCompanyId.value, selectedFile.value);
    toast("File uploaded successfully.");
    selectedFile.value = null;
    if (fileInput.value) fileInput.value.value = "";
    await Promise.all([loadFiles(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to upload file.", "error");
  } finally {
    submitting.value.file = false;
  }
}

async function downloadFile(file) {
  if (!validCompanyId.value || !file?.id) return;
  try {
    const res = await companyEngagementService.downloadFile(validCompanyId.value, file.id);
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = file.file_name || "company-file";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to download file.", "error");
  }
}

async function deleteFile(file) {
  if (!validCompanyId.value || !file?.id) return;
  if (!window.confirm("Delete this file?")) return;
  submitting.value.fileDeleteId = file.id;
  try {
    await companyEngagementService.deleteFile(validCompanyId.value, file.id);
    toast("File deleted.");
    await Promise.all([loadFiles(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to delete file.", "error");
  } finally {
    submitting.value.fileDeleteId = null;
  }
}

async function submitEmail() {
  if (!validCompanyId.value) return;
  const payload = {
    to_email: String(emailForm.value.to_email || "").trim() || null,
    subject: String(emailForm.value.subject || "").trim(),
    body: String(emailForm.value.body || "").trim(),
  };
  if (!payload.subject) return toast("Email subject is required.", "error");
  if (!payload.body) return toast("Email body is required.", "error");
  submitting.value.email = true;
  try {
    await companyEngagementService.sendEmail(validCompanyId.value, payload);
    toast("Email sent.");
    emailForm.value = { to_email: payload.to_email || props.defaultEmail || "", subject: "", body: "" };
    await Promise.all([loadEmails(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to send email.", "error");
  } finally {
    submitting.value.email = false;
  }
}

async function loadAllVisibleData() {
  emailForm.value.to_email = props.defaultEmail || "";
  resetNoteForm();
  resetCallForm();
  await Promise.all([loadActivities(), loadNotes(), loadCalls(), loadFiles(), loadEmails()]);
}

watch(() => [props.companyId, props.defaultEmail], async () => {
  if (!validCompanyId.value) return;
  await loadAllVisibleData();
});

onMounted(async () => {
  if (!validCompanyId.value) return;
  await loadAllVisibleData();
});
</script>

<style scoped>
.nav-link {
  border: 0;
  background: transparent;
  width: 100%;
}

.whitespace-pre-line {
  white-space: pre-line;
}
</style>
